import styled from 'styled-components';
import { Button } from '@shared/ui/Button';

/**
 * Содержимое карточки договора в общем окне Modal. Окно шире обычного: в карточке график
 * платежей и сведения в две колонки. Параметра размера у Modal нет — ширину и фон окна
 * задаёт само содержимое, правилом для окна, прямым потомком которого оно стоит.
 */
export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  min-width: 0;

  &:focus {
    outline: none;
  }

  [role='dialog']:has(> &) {
    width: 100%;
    max-width: 1080px;
    background: ${({ theme }) => theme.colors.surface};
  }
`;

/** Верх карточки: вид и реквизиты договора, справа — «Удалить договор». */
export const CardTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: 16px;
`;

/** Загрузка и ошибка загрузки паспорта. */
export const StatusText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SubTitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const HeaderActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/** Кнопки шапки карточки: на узком экране встают друг под другом справа. */
export const HeaderButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 1 auto;
`;

/** «Удалить договор»: обычная кнопка с красной подписью — заметна, но не главная. */
export const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.colors.status.danger};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.status.danger};
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
`;

export const ConfirmText = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ConfirmActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(185px, 100%), 1fr));
  gap: 12px;
`;

export const KpiBox = styled.div`
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const KpiValue = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const KpiHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Сведения договора: на узком экране колонка сжимается до ширины окна. */
export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 14px;
`;

export const InfoCard = styled.div`
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
  padding-bottom: 6px;
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

export const InfoKey = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const InfoVal = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
`;

export const TextNote = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ScheduleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
  flex-wrap: wrap;
`;

/** Обе полосы прокрутки — в пределах окна, шапка таблицы остаётся на месте. */
export const TableWrap = styled.div`
  max-height: min(70vh, calc(100vh - 260px));
  overflow: auto;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tfoot td {
    font-weight: 700;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    border-bottom: none;
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  td.money {
    white-space: nowrap;
  }
`;

export const DueSubtext = styled.div`
  font-size: 11px;
  opacity: 0.75;
`;

export const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ErrorAlert = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: #ef444422;
  border: 1px solid #ef4444;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  font-size: 12px;
  margin: 0 0 12px 0;
`;
